import {
  Component,
  Input,
  Optional,
  Self,
  signal,
  HostListener,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NgControl, FormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@dental-ui/shared/i18n';
import { Subscription } from 'rxjs';

interface PanelStyle {
  top: string;
  left: string;
}

@Component({
  selector: 'lib-time-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.scss',
})
export class TimePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private langSub?: Subscription;

  @Input() label = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() errorMessage = '';

  readonly timePlaceholder = signal('SS:DD');
  readonly hourLabel = signal('Saat');
  readonly minuteLabel = signal('Dakika');

  readonly isOpen = signal(false);
  readonly panelAbove = signal(false);
  readonly panelStyle = signal<PanelStyle>({ top: '0px', left: '0px' });
  readonly selectedHour = signal<number | null>(null);
  readonly selectedMinute = signal<number | null>(null);

  /** Raw text in the editable input */
  inputText = '';

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (_value: string) => {
    // ControlValueAccessor interface requirement
  };
  private onTouched = () => {
    // ControlValueAccessor interface requirement
  };

  readonly inputId = `tp-input-${Math.random().toString(36).substring(2, 9)}`;

  readonly HOURS = Array.from({ length: 24 }, (_, i) => i);
  readonly MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

  private readonly elRef = inject(ElementRef);

  // ControlValueAccessor requires NgControl via constructor with @Self/@Optional
  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.updatePlaceholder();
    this.registerValidators();
    this.langSub = this.translateService.languageChange.subscribe(() => {
      this.updatePlaceholder();
      this.cdr.markForCheck();
    });
  }

  private registerValidators(): void {
    const control = this.ngControl?.control;
    if (!control) return;

    const existing = control.validator;
    control.setValidators([
      existing ?? (() => null),
      this.createTimeValidator(),
    ]);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private createTimeValidator(): ValidatorFn {
    return (): ValidationErrors | null => {
      const digits = this.inputText.replace(/\D/g, '');
      if (!digits.length) return null;
      if (digits.length < 4) return { incompleteTime: true };
      if (!this.parseDigitsToTime(digits)) return { invalidTime: true };
      return null;
    };
  }

  private updateControlValidity(): void {
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private updatePlaceholder(): void {
    const lang = this.translateService.getCurrentLanguage();
    this.timePlaceholder.set(lang === 'tr' ? 'SS:DD' : 'HH:MM');
    this.hourLabel.set(lang === 'tr' ? 'Saat' : 'Hour');
    this.minuteLabel.set(lang === 'tr' ? 'Dakika' : 'Minute');
  }

  get hasError(): boolean {
    if (this.errorMessage) return true;
    return !!(this.ngControl?.errors && this.ngControl?.touched);
  }

  get errorText(): string {
    const errors = this.ngControl?.errors;

    if (this.ngControl?.touched) {
      if (errors?.['incompleteTime']) {
        return this.translate('COMMON.INCOMPLETE_TIME', 'Please enter a complete time');
      }
      if (errors?.['invalidTime']) {
        return this.translate('COMMON.INVALID_TIME', 'Please enter a valid time');
      }
    }

    if (this.errorMessage) return this.errorMessage;

    if (errors?.['required']) {
      return this.translate('COMMON.REQUIRED', 'This field is required');
    }

    return '';
  }

  private translate(key: string, fallback: string): string {
    const text = this.translateService.getText(key);
    return text !== key ? text : fallback;
  }

  /** User typed in the text field — format as HH:MM while typing */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsBeforeCursor = input.value
      .slice(0, input.selectionStart ?? 0)
      .replace(/\D/g, '').length;
    const digits = input.value.replace(/\D/g, '').slice(0, 4);
    this.applyFormattedInput(input, digits, digitsBeforeCursor);
  }

  onInputKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      this.handleDeleteKey(event);
      return;
    }

    const allowedKeys = [
      'Tab', 'Enter', 'Escape',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End',
    ];
    if (allowedKeys.includes(event.key)) return;

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const input = event.target as HTMLInputElement;
    const pos = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? pos;
    const digits = input.value.replace(/\D/g, '');
    const replacing = end > pos;

    if (!replacing && digits.length >= 4) {
      event.preventDefault();
    }
  }

  private handleDeleteKey(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const pos = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? pos;
    const hasSelection = end > pos;

    if (hasSelection) return;

    if (event.key === 'Backspace' && pos > 0 && value[pos - 1] === ':') {
      event.preventDefault();
      const digitsBefore = value.slice(0, pos).replace(/\D/g, '').length;
      const allDigits = value.replace(/\D/g, '');
      if (digitsBefore > 0) {
        const newDigits = allDigits.slice(0, digitsBefore - 1) + allDigits.slice(digitsBefore);
        this.applyFormattedInput(input, newDigits, digitsBefore - 1);
      }
      return;
    }

    if (event.key === 'Delete' && pos < value.length && value[pos] === ':') {
      event.preventDefault();
      const digitsBefore = value.slice(0, pos).replace(/\D/g, '').length;
      const allDigits = value.replace(/\D/g, '');
      if (digitsBefore < allDigits.length) {
        const newDigits = allDigits.slice(0, digitsBefore) + allDigits.slice(digitsBefore + 1);
        this.applyFormattedInput(input, newDigits, digitsBefore);
      }
    }
  }

  onInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const pos = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? pos;
    const digitsBefore = input.value.slice(0, pos).replace(/\D/g, '').length;
    const digitsAfter = input.value.slice(end).replace(/\D/g, '');
    const pastedDigits = pasted.replace(/\D/g, '');
    const newDigits = (
      input.value.slice(0, pos).replace(/\D/g, '') +
      pastedDigits +
      digitsAfter
    ).slice(0, 4);

    this.applyFormattedInput(input, newDigits, digitsBefore + pastedDigits.length);
  }

  private applyFormattedInput(
    input: HTMLInputElement,
    digits: string,
    digitCursor: number,
  ): void {
    const formatted = this.formatDigitsAsTime(digits);
    this.inputText = formatted;

    const parsed = digits.length === 4 ? this.parseDigitsToTime(digits) : null;
    if (parsed) {
      const val = `${String(parsed.h).padStart(2, '0')}:${String(parsed.m).padStart(2, '0')}`;
      this.selectedHour.set(parsed.h);
      this.selectedMinute.set(parsed.m);
      this.onChange(val);
    } else {
      this.selectedHour.set(null);
      this.selectedMinute.set(null);
      this.onChange('');
    }

    const cursorPos = this.getCursorPositionForDigitIndex(
      Math.min(digitCursor, digits.length),
      formatted,
    );

    requestAnimationFrame(() => {
      input.setSelectionRange(cursorPos, cursorPos);
    });

    this.updateControlValidity();
  }

  private formatDigitsAsTime(digits: string): string {
    const d = digits.slice(0, 4);
    if (!d.length) return '';

    const hour = d.slice(0, 2);
    if (d.length <= 2) {
      return d.length === 2 ? `${hour}:` : hour;
    }

    return `${hour}:${d.slice(2)}`;
  }

  private getCursorPositionForDigitIndex(digitIndex: number, formatted: string): number {
    if (digitIndex <= 0) return 0;

    let digitsSeen = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        digitsSeen++;
        if (digitsSeen === digitIndex) {
          const hasColonAfter = formatted[i + 1] === ':';
          return hasColonAfter ? i + 2 : i + 1;
        }
      }
    }

    return formatted.length;
  }

  private parseDigitsToTime(digits: string): { h: number; m: number } | null {
    const h = +digits.slice(0, 2);
    const m = +digits.slice(2, 4);
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return { h, m };
  }

  onInputBlur(): void {
    this.onTouched();

    const digits = this.inputText.replace(/\D/g, '');
    const parsed = digits.length === 4 ? this.parseDigitsToTime(digits) : null;

    if (parsed) {
      const val = `${String(parsed.h).padStart(2, '0')}:${String(parsed.m).padStart(2, '0')}`;
      this.selectedHour.set(parsed.h);
      this.selectedMinute.set(parsed.m);
      this.inputText = val;
      this.onChange(val);
    } else if (!digits.length) {
      this.selectedHour.set(null);
      this.selectedMinute.set(null);
      this.inputText = '';
      this.onChange('');
    } else {
      this.selectedHour.set(null);
      this.selectedMinute.set(null);
      this.onChange('');
    }

    this.updateControlValidity();
  }

  togglePanel(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    if (!this.isOpen()) {
      this.computePanelPosition();
    }
    this.isOpen.update((v) => !v);
    this.onTouched();
  }

  private computePanelPosition(): void {
    const panelWidth = 200;
    const panelHeight = 240;
    const rect = (this.elRef.nativeElement as HTMLElement).getBoundingClientRect();

    // Yatay: sağda yeterli alan yoksa sağa hizala
    const spaceRight = window.innerWidth - rect.left;
    const rawLeft = spaceRight >= panelWidth + 8 ? rect.left : rect.right - panelWidth;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - panelWidth - 8));

    // Dikey: aşağıda yer yoksa yukarı aç
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < panelHeight + 8 && rect.top >= panelHeight + 8;
    this.panelAbove.set(above);
    const top = above ? rect.top - panelHeight - 6 : rect.bottom + 6;

    this.panelStyle.set({ top: `${top}px`, left: `${left}px` });
  }

  selectHour(event: Event, h: number): void {
    event.stopPropagation();
    this.selectedHour.set(h);
    // sadece input'u güncelle, paneli açık bırak
    const m = this.selectedMinute();
    if (m !== null) {
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      this.inputText = val;
      this.onChange(val);
      this.updateControlValidity();
    }
  }

  selectMinute(event: Event, m: number): void {
    event.stopPropagation();
    this.selectedMinute.set(m);
    const h = this.selectedHour() ?? 0;
    this.selectedHour.set(h);
    const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    this.inputText = val;
    this.onChange(val);
    this.updateControlValidity();
    setTimeout(() => this.isOpen.set(false), 120);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(e.target)) {
      this.isOpen.set(false);
    }
  }

  writeValue(value: string): void {
    if (value && /^\d{1,2}:\d{2}$/.test(value)) {
      const [h, m] = value.split(':').map(Number);
      this.selectedHour.set(h);
      this.selectedMinute.set(m);
      this.inputText = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    } else {
      this.selectedHour.set(null);
      this.selectedMinute.set(null);
      this.inputText = '';
    }
    this.updateControlValidity();
  }

  registerOnChange(fn: (v: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}
